import React, { useState } from 'react';
import ProfileDetailRow, {
    ProfileRowPlacementType,
} from '../profile/ProfileDetailRow';
import { ParticipantSummary, Connection, UserSummary, FamilySummary, PlaydateInvite } from '../../gql/types';
import styles from './SelectableConnectionRow.module.css';
import CircularIconButton from '../icon/CircularIconButton';
import sendIcon from "../../assets/icons/sendIcon.svg";
import detailsIcon from "../../assets/icons/detailsIcon.svg";
import plusIcon from "../../assets/icons/plusIcon.svg";
import FamilyProfileOverlaySheet from '../profile/family/FamilyProfileOverlaySheet';
import PlaydateInviteOrganizerCreationWorkflow from '../playdate/workflow/PlaydateInviteOrganizerCreationWorkflow';
import { useToastContext } from 'contexts/ToastContext';

type SelectableConnectionRowProps = {
    connection: Connection;
    isSelected: boolean;
    isSelectMode: boolean;
    onToggle: () => void;
    onOpenDetails?: () => void;
    onSendRequest?: () => void;
};

const isUserSummary = (participant: ParticipantSummary): participant is UserSummary => {
    return participant.__typename === "UserSummary";
};

const SelectableConnectionRow: React.FC<SelectableConnectionRowProps> = ({
    connection,
    isSelected,
    isSelectMode,
    onToggle,
    onOpenDetails,
    onSendRequest,
}) => {
    const [showInviteCreationView, setShowInviteCreatonView] = useState(false);
    const [showFamilyProfileView, setShowFamilyProfileView] = useState(false);
    const { triggerToastEvent } = useToastContext();

    const { participant } = connection;

    let name: string;
    let profilePictureUrl: string | undefined;

    switch (participant.__typename) {
        case "UserSummary":
            name = `${participant.firstName || ""} ${participant.lastName || ""}`.trim();
            profilePictureUrl = participant.profilePictureUrl || undefined;
            break;
        case "FamilySummary":
            name = participant.name === "" ? "Family" : participant.name;
            profilePictureUrl = participant.parents?.[0]?.profilePictureUrl || undefined;
            break;
        default:
            name = "";
    }

    const defaultOpenDetails = () => {
        setShowFamilyProfileView(true);
    };

    const defaultSendRequest = () => {
        setShowInviteCreatonView(true);
    };

    const handleOpenDetails = onOpenDetails ?? defaultOpenDetails;
    const handleSendRequest = onSendRequest ?? defaultSendRequest;

    return (
        <>
            <div className={styles.row}>
                {isSelectMode && (
                    <div
                        className={`${styles.checkContainer} ${isSelected ? styles.checked : styles.unChecked
                            }`}
                        onClick={onToggle}
                    >
                        {isSelected && (
                            <img src={plusIcon} alt="Selected" className={styles.plusIcon} />
                        )}
                    </div>
                )}

                <div className={`${styles.left} ${isSelectMode ? styles.leftShifted : ""}`}>
                    <ProfileDetailRow
                        name={name}
                        profilePictureUrl={profilePictureUrl}
                        altText={participant.id}
                        placement={ProfileRowPlacementType.LIST}
                        textSize='small'
                    />
                </div>

                <div className={styles.right}>
                    <CircularIconButton
                        onClick={handleOpenDetails}
                        className={styles.actionButton}
                        icon={
                            <img src={detailsIcon} className={styles.actionIcon} />
                        }
                        innerButtonClassName={styles.actionIcon}
                    />
                    <CircularIconButton
                        onClick={handleSendRequest}
                        icon={
                            <img src={sendIcon} className={styles.actionIcon} />
                        }
                        className={styles.actionButton}
                        innerButtonClassName={styles.actionIcon}
                    />
                </div>
            </div>
            {showInviteCreationView && (
                <PlaydateInviteOrganizerCreationWorkflow
                    featureParameters={{ connectionIDs: [connection.connectionID] }}
                    onBack={() => setShowInviteCreatonView(false)}
                    onDataChange={(success: boolean, invite?: PlaydateInvite) => {
                        if (success) {
                            triggerToastEvent();
                        }
                    }}
                    initialConnections={[connection.participant]}
                />
            )}
            {showFamilyProfileView && (
                <FamilyProfileOverlaySheet
                    onBack={() => setShowFamilyProfileView(false)}
                    connection={connection}
                />
            )}
        </>
    );
};

export default SelectableConnectionRow;
